import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { t, type Locale } from "../i18n";
import { SvgIcon } from "./SvgIcon";
import { BtnSpinner } from "./BtnSpinner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Contact {
  email: string;
  name: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidEmail(email: string): boolean {
  return email.includes("@") && email.includes(".");
}

function commitInput(chips: string[], raw: string): [string[], string] {
  const val = raw.trim().replace(/[,;]+$/, "").trim();
  if (val && !chips.includes(val)) return [[...chips, val], ""];
  return [chips, ""];
}

function filterContacts(contacts: Contact[], query: string): Contact[] {
  if (query.length < 2) return [];
  const q = query.toLowerCase();
  return contacts
    .filter(
      (c) =>
        c.email.toLowerCase().includes(q) ||
        (c.name ?? "").toLowerCase().includes(q)
    )
    .slice(0, 6);
}

// ---------------------------------------------------------------------------
// ChipInput — recipient chip field with contact autocomplete
// ---------------------------------------------------------------------------

function ChipInput({
  chips,
  input,
  onChips,
  onInput,
  contacts,
  placeholder,
  autoFocus,
}: {
  chips: string[];
  input: string;
  onChips: (v: string[]) => void;
  onInput: (v: string) => void;
  contacts: Contact[];
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = filterContacts(contacts, input);
  const showDropdown = matches.length > 0;

  function selectMatch(contact: Contact) {
    const addr = contact.name ? contact.email : contact.email;
    if (!chips.includes(addr)) {
      onChips([...chips, addr]);
    }
    onInput("");
    setActiveIdx(-1);
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Escape") {
        setActiveIdx(-1);
        onInput("");
        return;
      }
      if (e.key === "Enter" && activeIdx >= 0) {
        e.preventDefault();
        selectMatch(matches[activeIdx]);
        return;
      }
    }

    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      const [c, i] = commitInput(chips, input);
      onChips(c);
      onInput(i);
      setActiveIdx(-1);
    } else if (e.key === "Backspace" && input === "" && chips.length > 0) {
      onChips(chips.slice(0, -1));
    }
  }

  function onBlur() {
    // Delay to allow mousedown on dropdown items to fire first
    setTimeout(() => {
      if (input.trim()) {
        const [c, i] = commitInput(chips, input);
        onChips(c);
        onInput(i);
      }
      setActiveIdx(-1);
    }, 150);
  }

  function onInputChange(v: string) {
    onInput(v);
    setActiveIdx(-1);
  }

  return (
    <div className="chipWrap" style={{ position: "relative" }}>
      {chips.map((c, i) => (
        <span key={i} className="chip">
          {c}
          <button
            type="button"
            className="chipRemove"
            tabIndex={-1}
            onClick={() => onChips(chips.filter((_, idx) => idx !== i))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="chipInputField"
        value={input}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder={chips.length === 0 ? placeholder : undefined}
        autoFocus={autoFocus}
        autoComplete="off"
      />
      {showDropdown && (
        <div className="chipDropdown">
          {matches.map((c, i) => (
            <div
              key={c.email}
              className={`chipDropdownItem${i === activeIdx ? " chipDropdownItemActive" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectMatch(c);
              }}
            >
              {c.name ? (
                <>
                  <span className="chipDropdownName">{c.name}</span>
                  <span className="chipDropdownEmail">{c.email}</span>
                </>
              ) : (
                <span className="chipDropdownEmail">{c.email}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComposeModal
// ---------------------------------------------------------------------------

export function ComposeModal(props: {
  locale: Locale;
  fromEmail: string;
  contacts?: Contact[];

  isOpen: boolean;
  onClose: () => void;

  // Signatures must not change — cc/bcc forwarding to be wired in a later pass
  onSaveDraft: (draft: { to: string; subject: string; text: string }) => Promise<void>;
  onSend: (msg: { from: string; to: string[]; subject: string; text: string }) => Promise<void>;

  initialTo?: string;
  initialSubject?: string;
  initialText?: string;
}) {
  const contacts = props.contacts ?? [];

  const [toChips, setToChips] = useState<string[]>([]);
  const [toInput, setToInput] = useState("");
  const [ccChips, setCcChips] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState("");
  const [bccChips, setBccChips] = useState<string[]>([]);
  const [bccInput, setBccInput] = useState("");
  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);
  const [subject, setSubject] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);

  function resetForm() {
    setToChips([]);
    setToInput("");
    setCcChips([]);
    setCcInput("");
    setBccChips([]);
    setBccInput("");
    setShowCC(false);
    setShowBCC(false);
    setSubject("");
    setError(null);
    if (editorRef.current) editorRef.current.innerHTML = "";
  }

  useEffect(() => {
    if (!props.isOpen) return;
    setError(null);
    setToChips(props.initialTo ? [props.initialTo] : []);
    setToInput("");
    setCcChips([]);
    setCcInput("");
    setBccChips([]);
    setBccInput("");
    setShowCC(false);
    setShowBCC(false);
    setSubject(props.initialSubject ?? "");
    if (editorRef.current) {
      editorRef.current.innerHTML = props.initialText
        ? props.initialText.replace(/\n/g, "<br>")
        : "";
    }
  }, [props.isOpen, props.initialTo, props.initialSubject, props.initialText]);

  function getToList(): string[] {
    const extra = toInput.trim() ? [toInput.trim()] : [];
    return [...toChips, ...extra];
  }

  const toList = getToList();
  const hasValidTo = toList.length > 0 && toList.every(isValidEmail);
  const hasSubject = subject.trim().length > 0;
  const hasBody = (editorRef.current?.innerText ?? "").trim().length > 0;
  const canSend = hasValidTo && hasSubject && hasBody && !sending && !saving;

  function execCmd(cmd: string, value?: string) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
  }

  async function doSaveDraft() {
    setError(null);
    setSaving(true);
    try {
      const to = getToList();
      await props.onSaveDraft({
        to: to[0] ?? "",
        subject,
        text: editorRef.current?.innerText ?? "",
      });
    } catch {
      setError(t(props.locale, "compose.error.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function doSend() {
    setError(null);
    const toList = getToList();
    if (toList.length === 0) {
      setError(t(props.locale, "compose.error.toRequired"));
      return;
    }
    setSending(true);
    try {
      await props.onSend({
        from: props.fromEmail,
        to: toList,
        subject,
        text: editorRef.current?.innerHTML ?? "",
      });
      props.onClose();
      resetForm();
    } catch {
      setError(t(props.locale, "compose.error.sendFailed"));
    } finally {
      setSending(false);
    }
  }

  if (!props.isOpen) return null;

  return createPortal(
    <div className="modalBackdrop" role="dialog" aria-modal="true">
      <div className="modalCard composeModal">

        {/* Header */}
        <div className="modalHeader">
          <div className="modalTitle">{t(props.locale, "compose.title")}</div>
          <button className="iconBtn" onClick={props.onClose} title={t(props.locale, "compose.close")}>
            <SvgIcon name="x-circle" title="Close" />
          </button>
        </div>

        {/* Recipient + subject meta rows */}
        <div className="composeMeta">

          {/* FROM */}
          <div className="composeRow">
            <span className="composeRowLabel">{t(props.locale, "compose.from")}</span>
            <select className="composeInlineInput" value={props.fromEmail} disabled>
              {/* TODO: populate with aliases from API */}
              <option value={props.fromEmail}>{props.fromEmail}</option>
            </select>
          </div>

          {/* TO */}
          <div className="composeRow">
            <span className="composeRowLabel">{t(props.locale, "compose.to")}</span>
            <ChipInput
              chips={toChips}
              input={toInput}
              onChips={setToChips}
              onInput={setToInput}
              contacts={contacts}
              placeholder="name@example.com"
              autoFocus
            />
            <div className="composeRowToggles">
              {!showCC && (
                <button type="button" className="composeToggleBtn" onClick={() => setShowCC(true)}>
                  {t(props.locale, "compose.cc")}
                </button>
              )}
              {!showBCC && (
                <button type="button" className="composeToggleBtn" onClick={() => setShowBCC(true)}>
                  {t(props.locale, "compose.bcc")}
                </button>
              )}
            </div>
          </div>

          {/* CC */}
          {showCC && (
            <div className="composeRow">
              <span className="composeRowLabel">{t(props.locale, "compose.cc")}</span>
              <ChipInput
                chips={ccChips}
                input={ccInput}
                onChips={setCcChips}
                onInput={setCcInput}
                contacts={contacts}
                placeholder="name@example.com"
              />
            </div>
          )}

          {/* BCC */}
          {showBCC && (
            <div className="composeRow">
              <span className="composeRowLabel">{t(props.locale, "compose.bcc")}</span>
              <ChipInput
                chips={bccChips}
                input={bccInput}
                onChips={setBccChips}
                onInput={setBccInput}
                contacts={contacts}
                placeholder="name@example.com"
              />
            </div>
          )}

          {/* SUBJECT */}
          <div className="composeRow">
            <span className="composeRowLabel">{t(props.locale, "compose.subject")}</span>
            <input
              className="composeInlineInput"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
            />
          </div>

        </div>

        {/* Rich text area */}
        <div className="composeEditorWrap">
          <div className="richToolbar">
            <button
              type="button"
              className="richToolbarBtn"
              onMouseDown={(e) => { e.preventDefault(); execCmd("bold"); }}
            >
              <SvgIcon name="type-bold" title="Bold" style={{ width: 16, height: 16 }} />
            </button>
            <button
              type="button"
              className="richToolbarBtn"
              onMouseDown={(e) => { e.preventDefault(); execCmd("italic"); }}
            >
              <SvgIcon name="type-italic" title="Italic" style={{ width: 16, height: 16 }} />
            </button>
            <button
              type="button"
              className="richToolbarBtn"
              onMouseDown={(e) => { e.preventDefault(); execCmd("underline"); }}
            >
              <SvgIcon name="type-underline" title="Underline" style={{ width: 16, height: 16 }} />
            </button>
            <div className="richToolbarSep" />
            <button
              type="button"
              className="richToolbarBtn"
              onMouseDown={(e) => { e.preventDefault(); execCmd("insertUnorderedList"); }}
            >
              <SvgIcon name="list-ul" title="List" style={{ width: 16, height: 16 }} />
            </button>
            <button
              type="button"
              className="richToolbarBtn"
              onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Link URL:"); if (url) execCmd("createLink", url); }}
            >
              <SvgIcon name="link" title="Insert link" style={{ width: 16, height: 16 }} />
            </button>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="richToolbarBtn"
              title="Attach file"
              disabled
            >
              <SvgIcon name="paperclip" title="Attach file" style={{ width: 16, height: 16 }} />
            </button>
          </div>

          <div
            ref={editorRef}
            className="richEditor"
            contentEditable
            suppressContentEditableWarning
            data-placeholder={t(props.locale, "compose.message")}
          />
        </div>

        {error ? <div className="composeError">{error}</div> : null}

        {/* Footer */}
        <div className="modalFooter">
          <button className="btn" onClick={doSaveDraft} disabled={saving || sending}>
            <BtnSpinner loading={saving}>
              {saving ? t(props.locale, "compose.saving") : t(props.locale, "compose.saveDraft")}
            </BtnSpinner>
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn primary" onClick={doSend} disabled={!canSend}>
            <BtnSpinner loading={sending}>
              {sending ? t(props.locale, "compose.sending") : t(props.locale, "compose.send")}
            </BtnSpinner>
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
